'use client';

import { createFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleCombobox,
  Switch,
  Textarea,
} from '@commenda/kakashi';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import { Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { FormBuilderHook } from '../../hooks/useFormBuilder';
import type { FormModule, ModuleRepeatableSettings } from '@commenda/types';
import { getAllFieldIds } from '../../utils/dsl-traversal';
import { VisibilityEditor } from './VisibilityEditor';

export function ModuleEditor({
  module,
  stepId,
  formBuilder,
}: {
  module: FormModule;
  stepId: string;
  formBuilder: FormBuilderHook;
}) {
  const { setNotification } = useNotification();
  const [name, setName] = useState(module.name);
  const [description, setDescription] = useState(module.description || '');
  const [repeatableEnabled, setRepeatableEnabled] = useState(
    module.repeatable?.enabled ?? false
  );
  const [repeatableMin, setRepeatableMin] = useState(
    module.repeatable?.min ?? 1
  );
  const [repeatableMax, setRepeatableMax] = useState(
    module.repeatable?.max ?? 10
  );
  const [repeatableAddButtonLabel, setRepeatableAddButtonLabel] = useState(
    module.repeatable?.addButtonLabel ?? 'Add Another'
  );
  const [repeatableAddButtonVariant, setRepeatableAddButtonVariant] = useState<
    'primary' | 'secondary' | 'tertiary' | 'link'
  >(module.repeatable?.addButtonVariant ?? 'secondary');
  const [repeatableAddButtonSize, setRepeatableAddButtonSize] = useState<
    'sm' | 'md' | 'lg' | 'xl'
  >(module.repeatable?.addButtonSize ?? 'sm');
  const [repeatableAddButtonFullWidth, setRepeatableAddButtonFullWidth] =
    useState(module.repeatable?.addButtonFullWidth ?? true);
  const [repeatableAddButtonAlignment, setRepeatableAddButtonAlignment] =
    useState<'left' | 'center' | 'right'>(
      module.repeatable?.addButtonAlignment ?? 'left'
    );
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFormData, setSaveFormData] = useState({
    name: module.name || 'Untitled Module',
    description: '',
    status: 'DRAFT' as FormsTemplateStatus,
    version: '1.0.0',
    versionNote: '',
  });

  useEffect(() => {
    setName(module.name);
    setDescription(module.description || '');
    setRepeatableEnabled(module.repeatable?.enabled ?? false);
    setRepeatableMin(module.repeatable?.min ?? 1);
    setRepeatableMax(module.repeatable?.max ?? 10);
    setRepeatableAddButtonLabel(
      module.repeatable?.addButtonLabel ?? 'Add Another'
    );
    setRepeatableAddButtonVariant(
      module.repeatable?.addButtonVariant ?? 'secondary'
    );
    setRepeatableAddButtonSize(module.repeatable?.addButtonSize ?? 'sm');
    setRepeatableAddButtonFullWidth(
      module.repeatable?.addButtonFullWidth ?? true
    );
    setRepeatableAddButtonAlignment(
      module.repeatable?.addButtonAlignment ?? 'left'
    );
  }, [module]);

  const buildRepeatableUpdate = (): ModuleRepeatableSettings => ({
    enabled: true,
    min: repeatableMin,
    max: repeatableMax,
    minFromField: module.repeatable?.minFromField,
    maxFromField: module.repeatable?.maxFromField,
    addButtonLabel: repeatableAddButtonLabel,
    addButtonVariant: repeatableAddButtonVariant,
    addButtonSize: repeatableAddButtonSize,
    addButtonFullWidth: repeatableAddButtonFullWidth,
    addButtonAlignment: repeatableAddButtonAlignment,
  });

  const handleUpdate = (updates: Partial<FormModule>) => {
    formBuilder.updateModule(stepId, module.id, updates);
  };

  const handleSaveAsModule = async () => {
    if (!saveFormData.name) {
      setNotification({
        title: 'Error',
        message: 'Please enter a module name',
        valid: false,
        show: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const result = await createFormsTemplate({
        name: saveFormData.name,
        description: saveFormData.description || undefined,
        type: FormsTemplateType.FORM_MODULE,
        status: saveFormData.status,
        dsl: module as unknown as Record<string, unknown>,
        version: saveFormData.version,
        versionNote: saveFormData.versionNote || undefined,
        key: module.saveKey || module.id,
      });

      if (result.success) {
        setShowSaveDialog(false);
        setNotification({
          title: 'Success',
          message: 'Module saved successfully!',
          valid: true,
          show: true,
        });
      } else {
        setNotification({
          title: 'Error',
          message: 'Failed to save module. Please try again.',
          valid: false,
          show: true,
        });
      }
    } catch (error) {
      console.error('Error saving module:', error);
      setNotification({
        title: 'Error',
        message: 'Failed to save module. Please try again.',
        valid: false,
        show: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-primary border-l border-border-secondary overflow-hidden">
      <div className="p-2.5 px-4 border-b border-border-secondary flex items-center justify-between">
        <div>
          <h2 className="txt-medium-medium text-text-primary">
            Section Settings
          </h2>
        </div>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          onClick={() => formBuilder.clearSelection()}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Section Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => handleUpdate({ name })}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() =>
                handleUpdate({ description: description || undefined })
              }
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="txt-small-medium text-text-primary">
                Make Repeatable
              </p>
              <p className="txt-xs-regular text-text-tertiary">
                Allow users to add multiple instances of this section
              </p>
            </div>
            <Switch
              checked={repeatableEnabled}
              onCheckedChange={(checked) => {
                setRepeatableEnabled(checked);
                const repeatable: ModuleRepeatableSettings | undefined = checked
                  ? buildRepeatableUpdate()
                  : undefined;
                handleUpdate({ repeatable });
              }}
            />
          </div>

          {repeatableEnabled && (
            <div className="space-y-4 border-border-secondary">
              <div className="space-y-2">
                <Label htmlFor="repeatableMin">Minimum instances</Label>
                <Input
                  id="repeatableMin"
                  type="number"
                  min={1}
                  value={repeatableMin}
                  onChange={(e) =>
                    setRepeatableMin(
                      Math.max(1, parseInt(e.target.value, 10) || 1)
                    )
                  }
                  onBlur={() => {
                    const min = Math.min(repeatableMin, repeatableMax);
                    const max = Math.max(repeatableMin, repeatableMax);
                    setRepeatableMin(min);
                    setRepeatableMax(max);
                    handleUpdate({
                      repeatable: { ...buildRepeatableUpdate(), min, max },
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="repeatableMax">Maximum instances</Label>
                <Input
                  id="repeatableMax"
                  type="number"
                  min={1}
                  value={repeatableMax}
                  onChange={(e) =>
                    setRepeatableMax(
                      Math.max(1, parseInt(e.target.value, 10) || 1)
                    )
                  }
                  onBlur={() => {
                    const min = Math.min(repeatableMin, repeatableMax);
                    const max = Math.max(repeatableMin, repeatableMax);
                    setRepeatableMin(min);
                    setRepeatableMax(max);
                    handleUpdate({
                      repeatable: { ...buildRepeatableUpdate(), min, max },
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="repeatableMinFromField">
                  Min from field (optional)
                </Label>
                <Select
                  value={module.repeatable?.minFromField ?? '__none__'}
                  onValueChange={(value) =>
                    handleUpdate({
                      repeatable: {
                        ...buildRepeatableUpdate(),
                        minFromField: value === '__none__' ? undefined : value,
                      },
                    })
                  }
                >
                  <SelectTrigger id="repeatableMinFromField">
                    <SelectValue placeholder="Use static min" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Use static min</SelectItem>
                    {getAllFieldIds(formBuilder.dsl).map((f) => (
                      <SelectItem key={f.fieldId} value={f.fieldId}>
                        {f.label || f.fieldId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="txt-xs-regular text-text-tertiary">
                  Field value overrides minimum instances (e.g. number of
                  subsidiaries)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="repeatableMaxFromField">
                  Max from field (optional)
                </Label>
                <Select
                  value={module.repeatable?.maxFromField ?? '__none__'}
                  onValueChange={(value) =>
                    handleUpdate({
                      repeatable: {
                        ...buildRepeatableUpdate(),
                        maxFromField: value === '__none__' ? undefined : value,
                      },
                    })
                  }
                >
                  <SelectTrigger id="repeatableMaxFromField">
                    <SelectValue placeholder="Use static max" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Use static max</SelectItem>
                    {getAllFieldIds(formBuilder.dsl).map((f) => (
                      <SelectItem key={f.fieldId} value={f.fieldId}>
                        {f.label || f.fieldId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="txt-xs-regular text-text-tertiary">
                  Field value overrides maximum instances
                </p>
              </div>

              <div className="space-y-4 border-t border-border-secondary pt-4 mt-4">
                <p className="txt-small-medium text-text-primary">
                  Add Button Configuration
                </p>
                <div className="space-y-2">
                  <Label htmlFor="repeatableAddButtonLabel">Button Label</Label>
                  <Input
                    id="repeatableAddButtonLabel"
                    value={repeatableAddButtonLabel}
                    onChange={(e) =>
                      setRepeatableAddButtonLabel(e.target.value)
                    }
                    onBlur={() =>
                      handleUpdate({ repeatable: buildRepeatableUpdate() })
                    }
                    placeholder="Add Another"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="repeatableAddButtonVariant">
                    Button Variant
                  </Label>
                  <SingleCombobox
                    id="repeatableAddButtonVariant"
                    options={[
                      { label: 'Primary', value: 'primary' },
                      { label: 'Secondary', value: 'secondary' },
                      { label: 'Tertiary', value: 'tertiary' },
                      { label: 'Link', value: 'link' },
                    ]}
                    value={repeatableAddButtonVariant}
                    setValue={(val) => {
                      setRepeatableAddButtonVariant(
                        val as 'primary' | 'secondary' | 'tertiary' | 'link'
                      );
                      handleUpdate({
                        repeatable: {
                          ...buildRepeatableUpdate(),
                          addButtonVariant: val as
                            | 'primary'
                            | 'secondary'
                            | 'tertiary'
                            | 'link',
                        },
                      });
                    }}
                    placeholder="Select variant"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="repeatableAddButtonSize">Button Size</Label>
                  <SingleCombobox
                    id="repeatableAddButtonSize"
                    options={[
                      { label: 'Small', value: 'sm' },
                      { label: 'Medium', value: 'md' },
                      { label: 'Large', value: 'lg' },
                      { label: 'Extra Large', value: 'xl' },
                    ]}
                    value={repeatableAddButtonSize}
                    setValue={(val) => {
                      setRepeatableAddButtonSize(
                        val as 'sm' | 'md' | 'lg' | 'xl'
                      );
                      handleUpdate({
                        repeatable: {
                          ...buildRepeatableUpdate(),
                          addButtonSize: val as 'sm' | 'md' | 'lg' | 'xl',
                        },
                      });
                    }}
                    placeholder="Select size"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="txt-small-medium text-text-primary">
                      Full Width
                    </p>
                    <p className="txt-xs-regular text-text-tertiary">
                      Button spans full container width
                    </p>
                  </div>
                  <Switch
                    checked={repeatableAddButtonFullWidth}
                    onCheckedChange={(checked) => {
                      setRepeatableAddButtonFullWidth(checked);
                      handleUpdate({
                        repeatable: {
                          ...buildRepeatableUpdate(),
                          addButtonFullWidth: checked,
                        },
                      });
                    }}
                  />
                </div>
                {!repeatableAddButtonFullWidth && (
                  <div className="space-y-2">
                    <Label htmlFor="repeatableAddButtonAlignment">
                      Alignment
                    </Label>
                    <SingleCombobox
                      id="repeatableAddButtonAlignment"
                      options={[
                        { label: 'Left', value: 'left' },
                        { label: 'Center', value: 'center' },
                        { label: 'Right', value: 'right' },
                      ]}
                      value={repeatableAddButtonAlignment}
                      setValue={(val) => {
                        setRepeatableAddButtonAlignment(
                          val as 'left' | 'center' | 'right'
                        );
                        handleUpdate({
                          repeatable: {
                            ...buildRepeatableUpdate(),
                            addButtonAlignment: val as
                              | 'left'
                              | 'center'
                              | 'right',
                          },
                        });
                      }}
                      placeholder="Select alignment"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <VisibilityEditor
            visibility={module.visibility}
            availableFields={getAllFieldIds(formBuilder.dsl).filter(
              (f) => f.fieldId
            )}
            onChange={(visibility) => handleUpdate({ visibility })}
          />
        </div>

        {/* Save as Module Button */}
        <div className="pt-4 border-t border-border-secondary">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setSaveFormData((prev) => ({
                ...prev,
                name: module.name || 'Untitled Module',
              }));
              setShowSaveDialog(true);
            }}
            className="w-full"
          >
            <Save className="w-4 h-4 mr-2" />
            Save as Reusable Module
          </Button>
        </div>
      </div>

      {/* Save Module Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader className="flex flex-col gap-xs justify-start items-start">
            <DialogTitle>Save as Reusable Module</DialogTitle>
            <DialogDescription>
              Save this module to reuse it in other forms.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 px-6">
            <div className="space-y-2">
              <Label htmlFor="moduleName">Module Name *</Label>
              <Input
                id="moduleName"
                value={saveFormData.name}
                onChange={(e) =>
                  setSaveFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter module name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="moduleDescription">Description</Label>
              <Textarea
                id="moduleDescription"
                value={saveFormData.description}
                onChange={(e) =>
                  setSaveFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Enter module description"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="moduleStatus">Status</Label>
                <SingleCombobox
                  id="moduleStatus"
                  options={[
                    { label: 'Draft', value: FormsTemplateStatus.DRAFT },
                    { label: 'Live', value: FormsTemplateStatus.LIVE },
                    {
                      label: 'Completed',
                      value: FormsTemplateStatus.COMPLETED,
                    },
                  ]}
                  value={saveFormData.status}
                  setValue={(val) =>
                    setSaveFormData((prev) => ({
                      ...prev,
                      status: val as FormsTemplateStatus,
                    }))
                  }
                  placeholder="Select status"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="moduleVersion">Version</Label>
                <Input
                  id="moduleVersion"
                  value={saveFormData.version}
                  onChange={(e) =>
                    setSaveFormData((prev) => ({
                      ...prev,
                      version: e.target.value,
                    }))
                  }
                  placeholder="1.0.0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="moduleVersionNote">Version Note</Label>
              <Textarea
                id="moduleVersionNote"
                value={saveFormData.versionNote}
                onChange={(e) =>
                  setSaveFormData((prev) => ({
                    ...prev,
                    versionNote: e.target.value,
                  }))
                }
                placeholder="What's included in this module?"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveAsModule}
              disabled={isSaving || !saveFormData.name}
            >
              {isSaving ? 'Saving...' : 'Save Module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
